import React from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { BellAlertIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { StaffIncidentDelivery } from '../../types';

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

interface Props {
  deliveries: StaffIncidentDelivery[];
}

/**
 * Per-guardian notification/acknowledgment status — staff-only (the parent
 * view never sees who ELSE was notified, only their own acknowledge state).
 * One row per delivery, keyed by `recipientUserId` rather than name — the
 * backend delivery row doesn't carry a display name for the recipient
 * (only the child's), so each row is labelled "Guardian" with a stable
 * index rather than inventing a name the API never sends.
 *
 * `notifiedAt` means the record was made AVAILABLE (delivery row written),
 * NOT that push succeeded — deliberately not conflated with `pushStatus`
 * here, matching the backend's own distinction
 * (`incident-delivery-service.ts`). `acknowledgedAt` is the guardian's own
 * action and is independent of both.
 */
const IncidentNotificationStatus: React.FC<Props> = ({ deliveries }) => {
  return (
    <div data-testid="incident-notification-status" className="bg-white rounded-lg shadow p-5">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
        <BellAlertIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
        Guardian notifications
      </h2>

      {deliveries.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">
          No guardian has been notified yet (no linked guardian, or still a draft).
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100">
          {deliveries.map((d, idx) => (
            <li
              key={d.id}
              data-testid={`incident-notification-row-${d.id}`}
              className="py-2 flex items-center justify-between gap-3 text-sm"
            >
              <span className="text-gray-700">Guardian {idx + 1}</span>
              <div className="flex items-center gap-3 text-xs">
                <span
                  data-testid={`incident-notification-notified-${d.id}`}
                  className="text-gray-500"
                >
                  Notified {formatDateTime(d.notifiedAt)}
                </span>
                {d.acknowledgedAt ? (
                  <span
                    data-testid={`incident-notification-acknowledged-${d.id}`}
                    className="inline-flex items-center gap-1 text-emerald-700 font-medium"
                  >
                    <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Acknowledged {formatDateTime(d.acknowledgedAt)}
                  </span>
                ) : (
                  <span
                    data-testid={`incident-notification-acknowledged-${d.id}`}
                    className="text-amber-700"
                  >
                    Not yet acknowledged
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default IncidentNotificationStatus;
